import React, { useState, useContext, useCallback } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { categories, paymentTerms } from "../../../data/contractData";
import {
  FiUploadCloud,
  FiDollarSign,
  FiUser,
  FiFileText,
  FiPhone,
  FiMail,
  FiMapPin,
  FiCalendar,
  FiBriefcase,
  FiPlus,
  FiX,
} from "react-icons/fi";
import { FaAmazonPay } from "react-icons/fa";

// Add departments for an ICT company
const departments = [
  "Client Relations",
  "Software Development",
  "Network Infrastructure",
  "Cybersecurity",
  "Data Analytics",
  "Cloud Services",
  "IT Support",
  "Project Management",
  "Quality Assurance",
  "Research and Development",
  "Marketing",
  "Human Resources",
  "Finance",
  "Legal",
  "Executive",
];

const InputField = ({
  icon: Icon,
  label,
  name,
  type = "text",
  value,
  onChange,
  options,
  required = true,
}) => (
  <div className="flex flex-col space-y-1">
    <label htmlFor={name} className="text-gray-300 font-semibold text-sm">
      {label}
    </label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      {type === "select" ? (
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
        >
          <option value="">Select {label}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      )}
    </div>
  </div>
);

const ManualCreateForm = () => {
  const [formData, setFormData] = useState({
    category: "",
    "Contract Number": "",
    Parties: [""],
    startDate: "",
    endDate: "",
    "Section Or Department": "",
    Term: "",
    "Contract Description": "",
    clientName: "",
    clientAddress: "",
    clientPostalCode: "",
    clientLocation: "",
    clientPhone: "",
    clientEmail: "",
    paymentTerm: "",
    contractAmount: "",
  });

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { authState } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  }, []);

  const handlePartyChange = useCallback((index, value) => {
    setFormData((prevData) => {
      const newParties = [...prevData.Parties];
      newParties[index] = value;
      return { ...prevData, Parties: newParties };
    });
  }, []);

  const addParty = useCallback(() => {
    setFormData((prevData) => ({
      ...prevData,
      Parties: [...prevData.Parties, ""],
    }));
  }, []);

  const removeParty = useCallback((index) => {
    setFormData((prevData) => ({
      ...prevData,
      Parties: prevData.Parties.filter((_, i) => i !== index),
    }));
  }, []);

  const handleFileChange = useCallback((e) => {
    setFiles(Array.from(e.target.files));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFields = [
      "category",
      "Contract Number",
      "Parties",
      "startDate",
      "Section Or Department",
      "Term",
      "endDate",
      "Contract Description",
    ];
    const missingFields = requiredFields.filter((field) => !formData[field]);

    if (missingFields.length > 0) {
      toast.error(`Missing fields: ${missingFields.join(", ")}`);
      return;
    }

    if (files.length === 0) {
      toast.error("No files selected ⛔⛔");
      return;
    }

    const submissionData = new FormData();
    files.forEach((file) => {
      submissionData.append("contracts", file);
    });

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "Parties") {
        submissionData.append(key, value.join(","));
      } else {
        submissionData.append(key, value);
      }
    });

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bulk/submit-manual-contract`,
        submissionData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "x-auth-token": authState.token,
          },
        }
      );

      toast.success(response.data.message);
      navigate("/dashboard");
    } catch (error) {
      if (error.response) {
        toast.error(`Error: ${error.response.data.message}`);
      } else {
        toast.error("An unexpected error occurred ⛔⛔");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8 py-12">
      <div className="w-full max-w-7xl mt-8 p-8 bg-gray-900 rounded-xl shadow-2xl">
        <h2 className="text-4xl font-extrabold text-center mb-8 text-purple-400">
          Manual Contract Entry
        </h2>
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-300">
              Contract Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InputField
                icon={FiFileText}
                label="Contract Number"
                name="Contract Number"
                value={formData["Contract Number"]}
                onChange={handleChange}
              />
              <InputField
                icon={FiFileText}
                label="Category"
                name="category"
                type="select"
                value={formData.category}
                onChange={handleChange}
                options={categories}
              />
              <InputField
                icon={FiBriefcase}
                label="Section Or Department"
                name="Section Or Department"
                type="select"
                value={formData["Section Or Department"]}
                onChange={handleChange}
                options={departments}
              />
              <InputField
                icon={FiCalendar}
                label="Start Date"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
              />
              <InputField
                icon={FiCalendar}
                label="End Date"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
              />
              <InputField
                icon={FiFileText}
                label="Contract Term"
                name="Term"
                value={formData.Term}
                onChange={handleChange}
              />
            </div>

            <div className="space-y-2">
              <label className="text-gray-300 font-semibold text-sm">
                Parties
              </label>
              {formData.Parties.map((party, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={party}
                    onChange={(e) => handlePartyChange(index, e.target.value)}
                    className="flex-grow pl-10 pr-4 py-2 bg-gray-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    placeholder={`Enter party ${index + 1}...`}
                    required
                  />
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeParty(index)}
                      className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none"
                    >
                      <FiX />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addParty}
                className="mt-2 flex items-center space-x-2 text-purple-400 hover:text-purple-300 focus:outline-none"
              >
                <FiPlus /> <span>Add Party</span>
              </button>
            </div>
            <div className="flex flex-col space-y-1">
              <label
                htmlFor="Contract Description"
                className="text-gray-300 font-semibold text-sm"
              >
                Contract Description
              </label>
              <textarea
                id="Contract Description"
                name="Contract Description"
                value={formData["Contract Description"]}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                rows="4"
                placeholder="Enter contract description..."
              />
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-300">Client Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InputField
                icon={FiUser}
                label="Client Name"
                name="clientName"
                value={formData.clientName}
                onChange={handleChange}
              />
              <InputField
                icon={FiMapPin}
                label="Client Address"
                name="clientAddress"
                value={formData.clientAddress}
                onChange={handleChange}
              />
              <InputField
                icon={FiMapPin}
                label="Client Postal Code"
                name="clientPostalCode"
                value={formData.clientPostalCode}
                onChange={handleChange}
              />
              <InputField
                icon={FiMapPin}
                label="Client Location"
                name="clientLocation"
                value={formData.clientLocation}
                onChange={handleChange}
              />
              <InputField
                icon={FiPhone}
                label="Client Phone"
                name="clientPhone"
                type="tel"
                value={formData.clientPhone}
                onChange={handleChange}
              />
              <InputField
                icon={FiMail}
                label="Client Email"
                name="clientEmail"
                type="email"
                value={formData.clientEmail}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-300">
              Payment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <InputField
                icon={FaAmazonPay}
                label="Payment Term"
                name="paymentTerm"
                type="select"
                value={formData.paymentTerm}
                onChange={handleChange}
                options={paymentTerms}
              />
              <InputField
                icon={FaAmazonPay}
                label="Contract Amount"
                name="contractAmount"
                type="number"
                value={formData.contractAmount}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-gray-300">
              Upload Contract Files
            </h3>
            <div className="flex items-center justify-center w-full">
              <label
                htmlFor="file-upload"
                className="w-full flex flex-col items-center px-4 py-6 bg-gray-800 text-white rounded-lg cursor-pointer hover:bg-gray-700 transition duration-300 ease-in-out"
              >
                <FiUploadCloud className="text-4xl mb-2" />
                <span className="mt-2 text-base leading-normal">
                  Select files to upload
                </span>
                <input
                  id="file-upload"
                  type="file"
                  className="hidden"
                  onChange={handleFileChange}
                  multiple
                  required
                />
              </label>
            </div>
            {files.length > 0 && (
              <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-400 mb-2">
                  {files.length} file(s) selected
                </p>
                <ul className="text-xs text-gray-500 max-h-40 overflow-y-auto">
                  {files.map((file, index) => (
                    <li key={index} className="truncate py-1">
                      {file.name}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg font-semibold shadow-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 transition duration-300"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit Contract"}
            </button>
          </div>
        </form>
        <Toaster position="bottom-center" />
      </div>
    </div>
  );
};

export default ManualCreateForm;
